import { api } from '@packages/backend/convex/_generated/api';
import { formatCents, getUserErrorMessage, type RegisterClosureCvx } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { ActionDropdown } from '@/components/ActionDropdown';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';

export const Route = createFileRoute('/r/$restaurantId/register-closures')({
  component: Component,
});

function Component() {
  const exportToExcel = (registerClosures: RegisterClosureCvx[]) => {
    const formattedRegisterClosures = registerClosures.map((r) => ({
      Date: format(r.closedAt, 'yyyy-MM-dd'),
      Time: format(r.closedAt, 'HH:mm'),
      CashExpected: formatCents(r.cashExpected),
      CashActual: formatCents(r.cashActual),
      CashDiff: formatCents(r.cashDiff),
      EftposExpected: formatCents(r.eftposExpected),
      EftposActual: formatCents(r.eftposActual),
      EftposDiff: formatCents(r.eftposDiff),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedRegisterClosures);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Register Closures');
    XLSX.writeFile(workbook, 'register-closures.xlsx');
  };

  const { restaurantId } = Route.useParams();
  const {
    data: registerClosures,
    isError,
    isPending,
  } = useQuery(api.registerClosures.listByRestaurantId, { restaurantId });

  if (isError) return null; // Handled in the parent layout

  return (
    <div className="p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col gap-4 bg-muted/60 dark:bg-muted/20">
      <Card>
        <CardHeader>
          <CardTitle>Register closures</CardTitle>
          <CardAction>
            {!isPending && registerClosures && registerClosures.length > 0 && (
              <ActionButton
                onClick={() => exportToExcel(registerClosures)}
                variant="outline"
                size="sm"
              >
                <Download className="h-4 w-4" />
                Excel File
              </ActionButton>
            )}
          </CardAction>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <LoadingIcon />
          ) : (
            <RegisterClosuresTable
              registerClosures={registerClosures}
              restaurantId={restaurantId}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const RegisterClosuresTable = ({
  registerClosures,
  restaurantId,
}: {
  registerClosures: RegisterClosureCvx[];
  restaurantId: string;
}) => {
  const convex = useConvex();

  const handleDelete = async (registerClosureId: string) => {
    try {
      await convex.mutation(api.registerClosures.deleteRegisterClosure, {
        restaurantId,
        registerClosureId,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Time</TableHead>
          <TableHead className="text-right">Cash expected</TableHead>
          <TableHead className="text-right">Cash actual</TableHead>
          <TableHead className="text-right">Cash difference</TableHead>

          <TableHead className="text-right">EFTPOS expected</TableHead>
          <TableHead className="text-right">EFTPOS actual</TableHead>
          <TableHead className="text-right">EFTPOS difference</TableHead>
          <SuperAdminOnly>
            <TableHead />
          </SuperAdminOnly>
        </TableRow>
      </TableHeader>
      <TableBody>
        {registerClosures.map((r) => {
          return (
            <TableRow key={r._id}>
              <TableCell className="py-0 whitespace-nowrap">
                <div className="font-medium">{format(r.closedAt, 'dd-MMM-yy, EEE')}</div>
              </TableCell>

              <TableCell className="py-0 whitespace-nowrap">
                <div className="font-medium">{format(r.closedAt, 'h:mm a')}</div>
              </TableCell>

              {/* Cash */}
              <TableCell className="py-0 text-right">
                {r.cashExpected === 0 ? '-' : formatCents(r.cashExpected, '$')}
              </TableCell>
              <TableCell className="py-0 text-right">
                {r.cashActual === 0 ? '-' : formatCents(r.cashActual, '$')}
              </TableCell>
              <TableCell className="py-0 bg-muted/60 text-right">
                {r.cashDiff === 0 ? (
                  <span className="text-green-600">balanced</span>
                ) : r.cashDiff > 0 ? (
                  `+${formatCents(r.cashDiff, '$')}`
                ) : (
                  formatCents(r.cashDiff, '$')
                )}
              </TableCell>

              {/* EFTPOS */}
              <TableCell className="py-0 text-right">
                {r.eftposExpected === 0 ? '-' : formatCents(r.eftposExpected, '$')}
              </TableCell>
              <TableCell className="py-0 text-right">
                {r.eftposActual === 0 ? '-' : formatCents(r.eftposActual, '$')}
              </TableCell>
              <TableCell className="font-medium py-0 bg-muted/60 text-right">
                {r.eftposDiff === 0 ? (
                  <span className="text-green-600">balanced</span>
                ) : r.eftposDiff > 0 ? (
                  `+${formatCents(r.eftposDiff, '$')}`
                ) : (
                  formatCents(r.eftposDiff, '$')
                )}
              </TableCell>
              <SuperAdminOnly>
                <TableCell className="py-0">
                  <ActionDropdown onDelete={() => handleDelete(r._id)} />
                </TableCell>
              </SuperAdminOnly>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
